<script setup lang="ts">
import { computed, useAttrs } from 'vue';

interface Props {
  id?: string;
  name: string;
  label?: string;
  placeholder?: string;
  modelValue?: string;
  type?: 'text' | 'email' | 'password' | 'tel' | 'url' | 'search';
  disabled?: boolean;
  readonly?: boolean;
  required?: boolean;
  autofocus?: boolean;
  autocomplete?: string;
  error?: boolean;
  errorMessage?: string;
  maxlength?: number;
  minlength?: number;
  pattern?: string;
  inputClass?: string;
  wrapperClass?: string;
  labelClass?: string;
}

const props = withDefaults(defineProps<Props>(), {
  type: 'text',
  disabled: false,
  readonly: false,
  required: false,
  autofocus: false,
  error: false,
  id: undefined,
  label: undefined,
  placeholder: undefined,
  modelValue: undefined,
  autocomplete: undefined,
  errorMessage: undefined,
  maxlength: undefined,
  minlength: undefined,
  pattern: undefined,
  inputClass: undefined,
  wrapperClass: undefined,
  labelClass: undefined,
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
  blur: [event: FocusEvent];
  focus: [event: FocusEvent];
  input: [event: Event];
  change: [event: Event];
}>();

const attrs = useAttrs();

const inputId = computed(() => {
  if (props.id) return props.id;
  return `input-${props.name}-${Math.random().toString(36).substring(2, 9)}`;
});

const errorId = computed(() => `${inputId.value}-error`);

const computedInputClass = computed(() => {
  const baseClass = props.error ? 'error-text-input w-full' : 'text-input w-full';
  return props.inputClass ? `${baseClass} ${props.inputClass}` : baseClass;
});

const computedWrapperClass = computed(() => {
  const baseClass = 'control-wrapper';
  return props.wrapperClass ? `${baseClass} ${props.wrapperClass}` : baseClass;
});

const computedLabelClass = computed(() => {
  const baseClass = 'block mb-1 text-sm font-medium text-grey';
  return props.labelClass ? `${baseClass} ${props.labelClass}` : baseClass;
});

const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  emit('update:modelValue', target.value);
  emit('input', event);
};

const handleBlur = (event: FocusEvent) => emit('blur', event);
const handleFocus = (event: FocusEvent) => emit('focus', event);
const handleChange = (event: Event) => emit('change', event);
</script>

<template>
  <div :class="computedWrapperClass">
    <label v-if="label" :for="inputId" :class="computedLabelClass">
      {{ label }}
    </label>
    <input
      :id="inputId"
      :name="name"
      :type="type"
      :placeholder="placeholder"
      :value="modelValue"
      :disabled="disabled"
      :readonly="readonly"
      :required="required"
      :autofocus="autofocus"
      :autocomplete="autocomplete"
      :maxlength="maxlength"
      :minlength="minlength"
      :pattern="pattern"
      :class="computedInputClass"
      :aria-invalid="error ? 'true' : undefined"
      :aria-describedby="error && errorMessage ? errorId : undefined"
      :aria-required="required ? 'true' : undefined"
      v-bind="attrs"
      @input="handleInput"
      @blur="handleBlur"
      @focus="handleFocus"
      @change="handleChange"
    />
    <div
      v-if="error && errorMessage"
      :id="errorId"
      class="bg-error p-2.5 pr-12 max-w-80 flex mt-2 gap-2.5 items-center text-white text-sm"
    >
      <i class="w-6 h-5 bg-white icon icon-warning shrink-0"></i>
      {{ errorMessage }}
    </div>
  </div>
</template>
